/*
 * Copyright (c) Schrocken Inc.
 * Author: Schrocken Dev
 * File Description: Daily Operations Dashboard - support login only.
 * Shows per-vendor operational metrics for a selected date range.
 */


import React, { Component } from 'react';
import axios from 'axios';
import { withStyles } from '@material-ui/core/styles';
import Grid from '@material-ui/core/Grid';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Table from '@material-ui/core/Table';
import TableBody from '@material-ui/core/TableBody';
import TableCell from '@material-ui/core/TableCell';
import TableContainer from '@material-ui/core/TableContainer';
import TableHead from '@material-ui/core/TableHead';
import TableRow from '@material-ui/core/TableRow';
import Paper from '@material-ui/core/Paper';
import moment from 'moment';


import CustomDatePickerMui from '../digitization/dashboard/CustomDatePickerMui';
import { appUrl } from '../../../../utils/config';

// Named constant for the admin API base path — same pattern used across all
// schrockenAdmin API calls (e.g. SyncForm). Centralised here so it is obvious
// this is an intentional internal route, not a user-supplied value.
const ADMIN_API_BASE = `${appUrl}/Pharma%20Contract%20Manufacturing/N1/sh1/Pharma%20Co/Admin`;
const DAILY_OPS_API_URL = `${ADMIN_API_BASE}/dailyOperationsDashboard`;


const styles = () => ({
  root: {
    padding: '1.5rem',
    backgroundColor: '#ffffff',
  },
  header: {
    marginBottom: '1.5rem',
    fontWeight: 600,
  },
  filterRow: {
    marginBottom: '1.5rem',
    alignItems: 'flex-end',
  },
  fieldLabel: {
    fontWeight: 500,
    marginBottom: '0.25rem',
    fontSize: '0.875rem',
  },
  viewReportBtn: {
    backgroundColor: '#f26522',
    color: '#ffffff',
    textTransform: 'none',
    fontWeight: 600,
    padding: '0.45rem 1.5rem',
    '&:hover': {
      backgroundColor: '#d4541a',
    },
    '&:disabled': {
      backgroundColor: '#f5a882',
      color: '#ffffff',
    },
  },
  tableContainer: {
    marginTop: '1rem',
    border: '1px solid #e0e0e0',
    borderRadius: 4,
  },
  tableHead: {
    backgroundColor: '#f5f5f5',
  },
  headCell: {
    fontWeight: 700,
    fontSize: '0.8rem',
    color: '#333333',
    borderRight: '1px solid #e0e0e0',
    padding: '10px 12px',
    lineHeight: 1.3,
  },
  bodyCell: {
    fontSize: '0.8rem',
    color: '#555555',
    borderRight: '1px solid #e0e0e0',
    padding: '8px 12px',
    textAlign: 'center',
  },
  titleCell: {
    fontSize: '0.8rem',
    color: '#333333',
    fontWeight: 500,
    borderRight: '1px solid #e0e0e0',
    padding: '8px 12px',
  },
  totalRow: {
    backgroundColor: '#f9f9f9',
  },
  totalTitleCell: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#333333',
    borderRight: '1px solid #e0e0e0',
    padding: '8px 12px',
  },
  totalCell: {
    fontSize: '0.8rem',
    fontWeight: 700,
    color: '#333333',
    borderRight: '1px solid #e0e0e0',
    padding: '8px 12px',
    textAlign: 'center',
  },
  errorText: {
    color: '#f44336',
    marginTop: '1rem',
  },
  loaderWrapper: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: '3rem',
  },
});


const COLUMNS = [
  { key: 'totalPOSynced', label: 'Total PO synced' },
  { key: 'batchAllocation', label: 'Batch allocation' },
  { key: 'cmoUpdatesPOs', label: 'CMO logs into Mosymphony and updates POs' },
  { key: 'posUpdatedInSAP', label: 'POs updated in SAP' },
  { key: 'digitizationCount', label: 'Digitization of key documents (PDFs)' },
  { key: 'inventoryReconciled', label: 'Inventory Reconciled' },
  { key: 'ibdCreation', label: 'IBD Creation' },
  { key: 'grnCreation', label: 'GRN Creation' },
];


class DailyOperationsDashboard extends Component {
  state = {
    startDate: null,
    endDate: null,
    loading: false,
    dashboardData: null,
    totals: null,
    error: null,
  };


  handleFetchReport = async () => {
    const { startDate, endDate } = this.state;
    this.setState({ loading: true, error: null, dashboardData: null, totals: null });


    try {
      // Dates come from the MUI date picker (always valid moment objects).
      // moment().isValid() guard adds defence-in-depth before the API call.
      if (!moment(startDate).isValid() || !moment(endDate).isValid()) {
        this.setState({ error: 'Invalid date selection. Please pick valid dates.', loading: false });
        return;
      }

      const payload = {
        startDate: moment(startDate).format('YYYY-MM-DD 00:00:00.0000000'),
        endDate: moment(endDate).format('YYYY-MM-DD 23:59:59.0000000'),
      };

      const res = await axios.post(DAILY_OPS_API_URL, payload);


      if (res.data.status) {
        this.setState({
          dashboardData: res.data.dashboardData,
          totals: res.data.totals,
        });
      } else {
        this.setState({ error: res.data.errorMessage || 'Failed to fetch data.' });
      }
    } catch (err) {
      this.setState({
        error:
          err.response?.data?.errorMessage ||
          err.message ||
          'An error occurred. Please try again.',
      });
    } finally {
      this.setState({ loading: false });
    }
  };


  render() {
    const { classes } = this.props;
    const { startDate, endDate, loading, dashboardData, totals, error } = this.state;


    const canFetch = startDate && endDate;


    return (
      <div className={classes.root}>
        <Typography variant='h6' className={classes.header}>
          Daily Operations
        </Typography>


        {/* Filter Row */}
        <Grid container spacing={2} className={classes.filterRow} alignItems='flex-end'>
          <Grid item xs={12} sm={6} md={3}>
            <Typography variant='body2' className={classes.fieldLabel}>
              Start Date:
            </Typography>
            <CustomDatePickerMui
              name='startDate'
              value={startDate}
              onChange={(date) => this.setState({ startDate: date })}
              placeholder='dd-mm-yyyy'
              maxDate={endDate || new Date()}
            />
          </Grid>


          <Grid item xs={12} sm={6} md={3}>
            <Typography variant='body2' className={classes.fieldLabel}>
              End Date:
            </Typography>
            <CustomDatePickerMui
              name='endDate'
              value={endDate}
              onChange={(date) => this.setState({ endDate: date })}
              placeholder='dd-mm-yyyy'
              minDate={startDate || undefined}
              maxDate={new Date()}
            />
          </Grid>


          <Grid item>
            <Button
              variant='contained'
              className={classes.viewReportBtn}
              onClick={this.handleFetchReport}
              disabled={!canFetch || loading}
            >
              {loading ? (
                <CircularProgress size={18} style={{ color: '#ffffff' }} />
              ) : (
                'View Report'
              )}
            </Button>
          </Grid>
        </Grid>


        {/* Error */}
        {error && (
          <Typography variant='body2' className={classes.errorText}>
            {error}
          </Typography>
        )}


        {/* Loading */}
        {loading && (
          <div className={classes.loaderWrapper}>
            <CircularProgress />
          </div>
        )}


        {/* Table */}
        {!loading && dashboardData && (
          <TableContainer component={Paper} className={classes.tableContainer}>
            <Table size='small' stickyHeader>
              <TableHead className={classes.tableHead}>
                <TableRow>
                  <TableCell className={classes.headCell}>Title</TableCell>
                  {COLUMNS.map((col) => (
                    <TableCell key={col.key} className={classes.headCell} align='center'>
                      {col.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {dashboardData.map((row) => (
                  <TableRow key={row.vendorMosymphonyId} hover>
                    <TableCell className={classes.titleCell}>
                      {row.vendorName}
                    </TableCell>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.key} className={classes.bodyCell}>
                        {row[col.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}


                {/* Total Count row */}
                {totals && (
                  <TableRow className={classes.totalRow}>
                    <TableCell className={classes.totalTitleCell}>
                      Total Count
                    </TableCell>
                    {COLUMNS.map((col) => (
                      <TableCell key={col.key} className={classes.totalCell}>
                        {totals[col.key]}
                      </TableCell>
                    ))}
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </div>
    );
  }
}


export default withStyles(styles)(DailyOperationsDashboard);